import React, { useState, useEffect, useRef, useCallback } from 'react'
import { backendService, FarmSummary } from '../../services/backendService'
import { DoctorSummary } from '../../services/types/farm'
import { logInfo, logError } from '../../utils/errorLogger'
import './FarmAITokens.css'

interface PresetEntry {
  id: string
  name: string
  slicer: string
  printer: string
  section: string
  nozzle?: string
  created_at: string
}

interface CatalogFile {
  filename: string
  name: string
  printer: string
  slicer: string
  section: string
  nozzle: string
  material: string
  inherits: string
  compatible_printers: string[]
  validated: boolean
  validated_by: string | null
  validated_at: string | null
}

type EntityType = 'doctor' | 'farm'

const SLICER_LABELS: Record<string, string> = {
  creality_print: 'Creality Print',
  orcaslicer: 'OrcaSlicer',
  bambu_studio: 'Bambu Studio',
}

// ── API helper ────────────────────────────────────────────────────────

async function apiAdmin(
  method: string,
  path: string,
  body?: FormData | null,
): Promise<Response> {
  const base = backendService.getBaseURL()
  const token = (backendService as unknown as { authToken: string | null }).authToken
  return fetch(`${base}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: body ?? undefined,
  })
}

async function apiAdminJSON(
  path: string,
  jsonBody: object,
): Promise<Response> {
  const base = backendService.getBaseURL()
  const token = (backendService as unknown as { authToken: string | null }).authToken
  return fetch(`${base}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(jsonBody),
  })
}

// ── Component ─────────────────────────────────────────────────────────

export const ManagePresets: React.FC = () => {
  // Entity type toggle
  const [entityType, setEntityType] = useState<EntityType>('farm')

  // Farms
  const [farms, setFarms] = useState<FarmSummary[]>([])
  const [selectedFarm, setSelectedFarm] = useState<FarmSummary | null>(null)

  // Doctors
  const [doctors, setDoctors] = useState<DoctorSummary[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorSummary | null>(null)

  // Shared
  const [presets, setPresets] = useState<PresetEntry[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  // Upload form
  const [slicer, setSlicer] = useState('creality_print')
  const [printer, setPrinter] = useState('K1')
  const [section, setSection] = useState('machine')
  const [nozzleFilter, setNozzleFilter] = useState('')
  const [uploadNozzle, setUploadNozzle] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  // Loading states
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isLoadingPresets, setIsLoadingPresets] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // ── Catalog browser state ───────────────────────────────────────────
  const [catalogTree, setCatalogTree] = useState<Record<string, any>>({})
  const [catalogFiles, setCatalogFiles] = useState<CatalogFile[]>([])
  const [catPrinter, setCatPrinter] = useState('')
  const [catSlicer, setCatSlicer] = useState('')
  const [catSection, setCatSection] = useState('')
  const [catNozzle, setCatNozzle] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set())
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [assignSuccess, setAssignSuccess] = useState(false)
  const [catalogSearch, setCatalogSearch] = useState('')

  // ── Load lists on mount & entityType change ─────────────────────────

  useEffect(() => {
    if (entityType === 'farm') {
      loadFarms()
    } else {
      loadDoctors()
    }
    setSelectedFarm(null)
    setSelectedDoctor(null)
    setPresets([])
    setUploadError(null)
    setUploadSuccess(false)
    setAssignError(null)
    setAssignSuccess(false)
  }, [entityType])

  // ── Load presets when selection changes ─────────────────────────────

  useEffect(() => {
    if (entityType === 'farm' && selectedFarm) {
      loadPresets(selectedFarm.id, nozzleFilter)
    } else if (entityType === 'doctor' && selectedDoctor) {
      loadPresets(selectedDoctor.id, nozzleFilter)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFarm, selectedDoctor, entityType, nozzleFilter])

  // ── Load catalog tree on mount ──────────────────────────────────────

  useEffect(() => {
    loadCatalogTree()
    // Intentional one-shot on mount; loadCatalogTree recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadCatalogTree = async () => {
    try {
      const res = await apiAdmin('GET', '/api/admin/presets/catalog')
      if (!res.ok) throw new Error(await res.text())
      const tree = await res.json()
      setCatalogTree(tree)
      const printers = Object.keys(tree)
      if (printers.length > 0 && !catPrinter) {
        setCatPrinter(printers[0])
      }
    } catch (e) {
      logError(`Failed to load catalog tree: ${e}`, 'ManagePresets')
    }
  }

  // ── Load catalog files when all 4 filters are set ────────────────────

  const loadCatalogFiles = useCallback(async () => {
    if (!catPrinter || !catSlicer || !catSection || !catNozzle) {
      setCatalogFiles([])
      return
    }
    setIsLoadingCatalog(true)
    try {
      const params = new URLSearchParams({
        printer: catPrinter,
        slicer_: catSlicer,
        section: catSection,
        nozzle: catNozzle,
      })
      const res = await apiAdmin('GET', `/api/admin/presets/catalog/list?${params}`)
      if (!res.ok) throw new Error(await res.text())
      setCatalogFiles(await res.json())
    } catch (e) {
      logError(`Failed to load catalog files: ${e}`, 'ManagePresets')
      setCatalogFiles([])
    } finally {
      setIsLoadingCatalog(false)
    }
  }, [catPrinter, catSlicer, catSection, catNozzle])

  useEffect(() => {
    loadCatalogFiles()
  }, [loadCatalogFiles])

  // ── Data loaders ────────────────────────────────────────────────────

  const loadFarms = async () => {
    setIsLoadingList(true)
    try {
      const res = await backendService.listAllFarms(1, 200, '', 'name', 'asc', false)
      setFarms(res.farms)
    } catch (e) {
      logError(`Failed to load farms: ${e}`, 'ManagePresets')
    } finally {
      setIsLoadingList(false)
    }
  }

  const loadDoctors = async () => {
    setIsLoadingList(true)
    try {
      const res = await backendService.listAllDoctors(1, 200, undefined, 'name', 'asc', true)
      setDoctors(res.doctors)
    } catch (e) {
      logError(`Failed to load doctors: ${e}`, 'ManagePresets')
    } finally {
      setIsLoadingList(false)
    }
  }

  const loadPresets = async (entityId: string, nozzle?: string) => {
    setIsLoadingPresets(true)
    setPresets([])
    try {
      let endpoint =
        entityType === 'farm'
          ? `/api/admin/farms/${entityId}/presets`
          : `/api/admin/doctors/${entityId}/presets`
      if (nozzle) endpoint += `?nozzle=${nozzle}`
      const res = await apiAdmin('GET', endpoint)
      if (!res.ok) throw new Error(await res.text())
      setPresets(await res.json())
      logInfo(`Loaded presets for ${entityType} ${entityId}`, 'ManagePresets')
    } catch (e) {
      logError(`Failed to load presets: ${e}`, 'ManagePresets')
    } finally {
      setIsLoadingPresets(false)
    }
  }

  // ── Upload handler ──────────────────────────────────────────────────

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    const entityId =
      entityType === 'farm' ? selectedFarm?.id : selectedDoctor?.id
    if (!entityId || !fileRef.current?.files?.[0]) return

    setIsUploading(true)
    setUploadError(null)
    setUploadSuccess(false)

    const form = new FormData()
    form.append('file', fileRef.current.files[0])
    form.append('slicer', slicer)
    form.append('printer', printer)
    form.append('section', section)
    if (uploadNozzle) form.append('nozzle', uploadNozzle)

    try {
      const endpoint =
        entityType === 'farm'
          ? `/api/admin/farms/${entityId}/presets`
          : `/api/admin/doctors/${entityId}/presets`
      const res = await apiAdmin('POST', endpoint, form)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        throw new Error(err.detail || res.statusText)
      }
      setUploadSuccess(true)
      if (fileRef.current) fileRef.current.value = ''
      await loadPresets(entityId)
      logInfo(`Preset uploaded for ${entityType} ${entityId}`, 'ManagePresets')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setUploadError(msg)
      logError(`Preset upload failed: ${msg}`, 'ManagePresets')
    } finally {
      setIsUploading(false)
    }
  }

  // ── Delete handler ──────────────────────────────────────────────────

  const handleDelete = async (presetId: string, presetName: string) => {
    const entityId =
      entityType === 'farm' ? selectedFarm?.id : selectedDoctor?.id
    if (!entityId) return
    if (!window.confirm(`Delete preset "${presetName}"?`)) return
    try {
      const endpoint =
        entityType === 'farm'
          ? `/api/admin/farms/${entityId}/presets/${presetId}`
          : `/api/admin/doctors/${entityId}/presets/${presetId}`
      const res = await apiAdmin('DELETE', endpoint)
      if (!res.ok && res.status !== 204) throw new Error(await res.text())
      await loadPresets(entityId)
      logInfo(`Deleted preset ${presetId}`, 'ManagePresets')
    } catch (err) {
      logError(`Failed to delete preset: ${err}`, 'ManagePresets')
    }
  }

  // ── Catalog assign handler ───────────────────────────────────────────

  const handleAssignFromCatalog = async () => {
    const entityId =
      entityType === 'farm' ? selectedFarm?.id : selectedDoctor?.id
    if (!entityId || selectedFiles.size === 0) return

    setIsAssigning(true)
    setAssignError(null)
    setAssignSuccess(false)

    try {
      const endpoint =
        entityType === 'farm'
          ? `/api/admin/farms/${entityId}/presets/from-catalog`
          : `/api/admin/doctors/${entityId}/presets/from-catalog`

      let successCount = 0
      let failCount = 0
      for (const filename of selectedFiles) {
        const res = await apiAdminJSON(endpoint, {
          printer: catPrinter,
          slicer: catSlicer,
          section: catSection,
          nozzle: catNozzle,
          filename,
        })
        if (res.ok) {
          successCount++
        } else {
          failCount++
          const err = await res.json().catch(() => ({ detail: res.statusText }))
          logError(`Failed to assign ${filename}: ${err.detail}`, 'ManagePresets')
        }
      }

      if (failCount === 0) {
        setAssignSuccess(true)
      } else {
        setAssignError(`${failCount} preset(s) failed to assign. ${successCount} succeeded.`)
      }
      setSelectedFiles(new Set())
      await loadPresets(entityId)
      logInfo(`Assigned ${successCount} catalog presets to ${entityType} ${entityId}`, 'ManagePresets')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setAssignError(msg)
      logError(`Catalog assign failed: ${msg}`, 'ManagePresets')
    } finally {
      setIsAssigning(false)
    }
  }

  // ── Filtered lists ──────────────────────────────────────────────────

  const filteredFarms = farms.filter(f =>
    f.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.ownerName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredDoctors = doctors.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // ── Catalog cascading options ───────────────────────────────────────

  const catPrinters = Object.keys(catalogTree)
  const catSlicers = catPrinter && catalogTree[catPrinter] ? Object.keys(catalogTree[catPrinter]) : []
  const catSections = catPrinter && catSlicer && catalogTree[catPrinter]?.[catSlicer]
    ? Object.keys(catalogTree[catPrinter][catSlicer]) : []
  const catNozzles = catPrinter && catSlicer && catSection && catalogTree[catPrinter]?.[catSlicer]?.[catSection]
    ? Object.keys(catalogTree[catPrinter][catSlicer][catSection]) : []

  // ── Filtered catalog files (by search) ──────────────────────────────

  const filteredCatalogFiles = catalogFiles.filter(f => {
    if (!catalogSearch) return true
    const q = catalogSearch.toLowerCase()
    return f.name.toLowerCase().includes(q) ||
           f.filename.toLowerCase().includes(q) ||
           f.material.toLowerCase().includes(q)
  })

  // ── Which entity is selected? ───────────────────────────────────────

  const hasSelection = entityType === 'farm' ? !!selectedFarm : !!selectedDoctor
  const selectedLabel =
    entityType === 'farm'
      ? selectedFarm?.businessName ?? ''
      : selectedDoctor?.name ?? ''
  const emptyText =
    entityType === 'farm'
      ? 'No custom presets assigned to this farm.'
      : 'No custom presets assigned to this doctor.'
  const placeholderText = 'Select a doctor or farm to manage its presets.'
  const subtitleText =
    entityType === 'farm'
      ? 'Farm-specific preset files — only visible to this farm'
      : 'Doctor-specific preset files — only visible to this doctor'

  // ── Toggle file selection ────────────────────────────────────────────

  const toggleFileSelection = (filename: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev)
      if (next.has(filename)) next.delete(filename)
      else next.add(filename)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedFiles.size === filteredCatalogFiles.length) {
      setSelectedFiles(new Set())
    } else {
      setSelectedFiles(new Set(filteredCatalogFiles.map(f => f.filename)))
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div className="farm-ai-tokens">
      {/* Left panel — entity list */}
      <div className="farm-ai-tokens__list-panel">
        <div className="farm-ai-tokens__search">
          <input
            type="text"
            placeholder={entityType === 'farm' ? 'Search farms...' : 'Search doctors...'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="admin-search-input"
          />
        </div>

        {isLoadingList && (
          <p className="farm-ai-tokens__loading">
            Loading {entityType === 'farm' ? 'farms' : 'doctors'}...
          </p>
        )}

        {entityType === 'farm' && (
          <ul className="farm-ai-tokens__farm-list">
            {filteredFarms.map(farm => (
              <li
                key={farm.id}
                className={`farm-ai-tokens__farm-item${selectedFarm?.id === farm.id ? ' farm-ai-tokens__farm-item--active' : ''}`}
                onClick={() => { setSelectedFarm(farm); setSelectedDoctor(null) }}
              >
                <div className="farm-ai-tokens__farm-name">{farm.businessName}</div>
                <div className="farm-ai-tokens__farm-meta">{farm.city}, {farm.country}</div>
              </li>
            ))}
          </ul>
        )}

        {entityType === 'doctor' && (
          <ul className="farm-ai-tokens__farm-list">
            {filteredDoctors.map(doctor => (
              <li
                key={doctor.id}
                className={`farm-ai-tokens__farm-item${selectedDoctor?.id === doctor.id ? ' farm-ai-tokens__farm-item--active' : ''}`}
                onClick={() => { setSelectedDoctor(doctor); setSelectedFarm(null) }}
              >
                <div className="farm-ai-tokens__farm-name">{doctor.name}</div>
                <div className="farm-ai-tokens__farm-meta">{doctor.username} &middot; {doctor.email}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right panel */}
      <div className="farm-ai-tokens__detail-panel">
        {/* Entity type toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderRadius: 8, overflow: 'hidden', border: '1px solid #cbd5e1', width: 'fit-content' }}>
          <button
            type="button"
            onClick={() => setEntityType('farm')}
            style={{
              padding: '8px 24px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
              background: entityType === 'farm' ? '#06b6d4' : '#f1f5f9',
              color: entityType === 'farm' ? '#ffffff' : '#475569',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            Farm
          </button>
          <button
            type="button"
            onClick={() => setEntityType('doctor')}
            style={{
              padding: '8px 24px',
              border: 'none',
              borderLeft: '1px solid #cbd5e1',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
              background: entityType === 'doctor' ? '#06b6d4' : '#f1f5f9',
              color: entityType === 'doctor' ? '#ffffff' : '#475569',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            Doctor
          </button>
        </div>

        {!hasSelection && (
          <p className="farm-ai-tokens__placeholder">{placeholderText}</p>
        )}

        {hasSelection && (
          <>
            <div className="farm-ai-tokens__farm-header">
              <h2>{selectedLabel}</h2>
              <span>{subtitleText}</span>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* CATALOG BROWSER — Smart menu for preset allocation           */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="preset-catalog-browser" style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: 16,
              marginBottom: 20,
            }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                📦 Preset Library — Select from Catalog
              </h3>

              {/* Cascading filter dropdowns */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {/* Printer filter */}
                <div className="form-group" style={{ flex: '1 1 120px' }}>
                  <label className="form-label" style={{ fontSize: '0.75rem', color: '#64748b' }}>Printer</label>
                  <select
                    className="admin-input"
                    style={{ fontSize: '0.85rem' }}
                    value={catPrinter}
                    onChange={e => {
                      setCatPrinter(e.target.value)
                      setCatSlicer('')
                      setCatSection('')
                      setCatNozzle('')
                      setSelectedFiles(new Set())
                    }}
                  >
                    <option value="">— Select —</option>
                    {catPrinters.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                {/* Slicer filter */}
                <div className="form-group" style={{ flex: '1 1 140px' }}>
                  <label className="form-label" style={{ fontSize: '0.75rem', color: '#64748b' }}>Slicer</label>
                  <select
                    className="admin-input"
                    style={{ fontSize: '0.85rem' }}
                    value={catSlicer}
                    disabled={!catPrinter}
                    onChange={e => {
                      setCatSlicer(e.target.value)
                      setCatSection('')
                      setCatNozzle('')
                      setSelectedFiles(new Set())
                    }}
                  >
                    <option value="">— Select —</option>
                    {catSlicers.map(s => <option key={s} value={s}>{SLICER_LABELS[s] ?? s}</option>)}
                  </select>
                </div>

                {/* Section filter */}
                <div className="form-group" style={{ flex: '1 1 120px' }}>
                  <label className="form-label" style={{ fontSize: '0.75rem', color: '#64748b' }}>Section</label>
                  <select
                    className="admin-input"
                    style={{ fontSize: '0.85rem' }}
                    value={catSection}
                    disabled={!catSlicer}
                    onChange={e => {
                      setCatSection(e.target.value)
                      setCatNozzle('')
                      setSelectedFiles(new Set())
                    }}
                  >
                    <option value="">— Select —</option>
                    {catSections.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>

                {/* Nozzle filter */}
                <div className="form-group" style={{ flex: '1 1 100px' }}>
                  <label className="form-label" style={{ fontSize: '0.75rem', color: '#64748b' }}>Nozzle</label>
                  <select
                    className="admin-input"
                    style={{ fontSize: '0.85rem' }}
                    value={catNozzle}
                    disabled={!catSection}
                    onChange={e => {
                      setCatNozzle(e.target.value)
                      setSelectedFiles(new Set())
                    }}
                  >
                    <option value="">— Select —</option>
                    {catNozzles.map(n => <option key={n} value={n}>{n}mm</option>)}
                  </select>
                </div>
              </div>

              {/* Search within results */}
              {catNozzle && (
                <div style={{ marginBottom: 8 }}>
                  <input
                    type="text"
                    placeholder="Filter presets by name..."
                    value={catalogSearch}
                    onChange={e => setCatalogSearch(e.target.value)}
                    className="admin-search-input"
                    style={{ fontSize: '0.8rem', padding: '4px 10px', width: '100%' }}
                  />
                </div>
              )}

              {/* File list with checkboxes */}
              {catNozzle && (
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
                  {isLoadingCatalog && (
                    <p style={{ padding: 12, color: '#64748b', fontSize: '0.85rem' }}>Loading presets...</p>
                  )}
                  {!isLoadingCatalog && filteredCatalogFiles.length === 0 && (
                    <p style={{ padding: 12, color: '#94a3b8', fontSize: '0.85rem' }}>No presets found at this path.</p>
                  )}
                  {filteredCatalogFiles.length > 0 && (
                    <>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 12px',
                        borderBottom: '1px solid #e2e8f0',
                        background: '#f1f5f9',
                        position: 'sticky', top: 0, zIndex: 1,
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedFiles.size === filteredCatalogFiles.length && filteredCatalogFiles.length > 0}
                          onChange={toggleSelectAll}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 600 }}>
                          {selectedFiles.size > 0
                            ? `${selectedFiles.size} selected`
                            : 'Select all'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem' }}>
                        {filteredCatalogFiles.map(f => (
                          <label
                            key={f.filename}
                            style={{
                              display: 'flex', alignItems: 'flex-start', gap: 8,
                              padding: '6px 12px',
                              borderBottom: '1px solid #f1f5f9',
                              cursor: 'pointer',
                              background: selectedFiles.has(f.filename) ? '#ecfeff' : 'transparent',
                              transition: 'background 0.1s',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFiles.has(f.filename)}
                              onChange={() => toggleFileSelection(f.filename)}
                              style={{ cursor: 'pointer', marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {f.name}
                              </div>
                              {f.material && (
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                  Material: {f.material}
                                </div>
                              )}
                              <div style={{ fontSize: '0.7rem', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                {f.validated ? (
                                  <span style={{ color: '#16a34a', fontWeight: 600 }}>
                                    ✓ Validated{f.validated_by ? ` by ${f.validated_by}` : ''}
                                  </span>
                                ) : (
                                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                                    ⚠ Not validated
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Assign button */}
              {selectedFiles.size > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={isAssigning}
                    onClick={handleAssignFromCatalog}
                    style={{
                      fontSize: '0.85rem',
                      padding: '8px 18px',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontWeight: 600,
                      background: '#06b6d4',
                      color: '#ffffff',
                      opacity: isAssigning ? 0.6 : 1,
                      transition: 'background 0.15s',
                    }}
                  >
                    {isAssigning
                      ? `Assigning ${selectedFiles.size} preset(s)...`
                      : `Assign ${selectedFiles.size} preset(s) to ${selectedLabel}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedFiles(new Set())}
                    style={{
                      fontSize: '0.85rem',
                      padding: '8px 18px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontWeight: 500,
                      background: '#ffffff',
                      color: '#475569',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1' }}
                  >
                    Clear
                  </button>
                </div>
              )}

              {assignError && <p className="farm-ai-tokens__error" style={{ marginTop: 8 }}>{assignError}</p>}
              {assignSuccess && (
                <p className="farm-ai-tokens__success" style={{ marginTop: 8 }}>
                  Presets assigned successfully.
                </p>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* MANUAL UPLOAD — secondary option                              */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <details style={{ marginBottom: 20 }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                ▸ Manual upload (custom file not in library)
              </summary>
              <form className="farm-presets-upload-form" onSubmit={handleUpload} style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div className="form-group" style={{ flex: '1 1 120px' }}>
                    <label className="form-label">Printer</label>
                    <select className="admin-input" value={printer} onChange={e => setPrinter(e.target.value)}>
                      <option>K1</option>
                      <option>K1Max</option>
                      <option>H2D</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: '1 1 150px' }}>
                    <label className="form-label">Slicer</label>
                    <select className="admin-input" value={slicer} onChange={e => setSlicer(e.target.value)}>
                      <option value="creality_print">Creality Print</option>
                      <option value="orcaslicer">OrcaSlicer</option>
                      <option value="bambu_studio">Bambu Studio</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: '1 1 120px' }}>
                    <label className="form-label">Section</label>
                    <select className="admin-input" value={section} onChange={e => setSection(e.target.value)}>
                      <option value="machine">Machine</option>
                      <option value="filament">Filament</option>
                      <option value="process">Process</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: '1 1 100px' }}>
                    <label className="form-label">Nozzle</label>
                    <select className="admin-input" value={uploadNozzle} onChange={e => setUploadNozzle(e.target.value)}>
                      <option value="">Auto-detect</option>
                      <option value="0.2">0.2mm</option>
                      <option value="0.4">0.4mm</option>
                      <option value="0.6">0.6mm</option>
                      <option value="0.8">0.8mm</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">JSON Preset File</label>
                  <input ref={fileRef} type="file" accept=".json" className="admin-input" required />
                </div>

                {uploadError && <p className="farm-ai-tokens__error">{uploadError}</p>}
                {uploadSuccess && <p className="farm-ai-tokens__success">Preset uploaded successfully.</p>}

                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={isUploading}
                  style={{ marginTop: 4 }}
                >
                  {isUploading ? 'Uploading...' : 'Upload Preset'}
                </button>
              </form>
            </details>

            {/* Preset list */}
            <div className="farm-ai-tokens__history" style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Assigned Presets</h3>
                <div className="form-group" style={{ flex: '0 0 140px', marginLeft: 'auto' }}>
                  <select
                    className="admin-input"
                    value={nozzleFilter}
                    onChange={e => setNozzleFilter(e.target.value)}
                    style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                  >
                    <option value="">All nozzles</option>
                    <option value="0.2">0.2mm</option>
                    <option value="0.4">0.4mm</option>
                    <option value="0.6">0.6mm</option>
                    <option value="0.8">0.8mm</option>
                  </select>
                </div>
              </div>
              {isLoadingPresets && <p className="farm-ai-tokens__empty">Loading...</p>}
              {!isLoadingPresets && presets.length === 0 && (
                <p className="farm-ai-tokens__empty">{emptyText}</p>
              )}
              {presets.length > 0 && (
                <table className="farm-ai-tokens__table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Printer</th>
                      <th>Slicer</th>
                      <th>Section</th>
                      <th>Nozzle</th>
                      <th>Uploaded</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {presets.map(p => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>{p.printer}</td>
                        <td>{SLICER_LABELS[p.slicer] ?? p.slicer}</td>
                        <td>{p.section}</td>
                        <td>{p.nozzle ? `${p.nozzle}mm` : '—'}</td>
                        <td>{new Date(p.created_at).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="admin-btn admin-btn-danger"
                            style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                            onClick={() => handleDelete(p.id, p.name)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
